/**
 * Sources service — a record's external-system links (`record_sources`).
 */

import type { ResultOf } from "@graphql-typed-document-node/core"
import {
  addRecordSource,
  detachAndRevertSource,
  fetchRecordSources,
  fetchSourceHolder,
  removeRecordSource,
} from "../gql/sources.js"
import type { Mutate, Query } from "../index.js"
import type { RecordRow } from "./records.js"

/** A source as `list` returns it: its id, the (type, id) pair, the cached
 *  context and the enrichment flag. */
export type ListedSource = ResultOf<typeof fetchRecordSources>["sources"][number]

/** A source as `add` returns it: RecordSourceFields. */
export type SourceRow = NonNullable<ResultOf<typeof addRecordSource>["insert_record_sources_one"]>

/**
 * Thrown by `sources.add` when the source already belongs to a DIFFERENT record
 * of the same type. The database raises it (enforce_record_source_uniqueness);
 * a duplicate on the SAME record is the table's plain unique constraint and is
 * not this error.
 */
export class SourceConflictError extends Error {
  readonly sourceType: string
  readonly sourceId: string
  /** The record the add was for. */
  readonly recordId: number
  /** The record that already holds the source, or null when the database didn't name it. */
  readonly conflictingRecordId: number | null
  constructor(input: {
    sourceType: string
    sourceId: string
    recordId: number
    conflictingRecordId: number | null
    cause: unknown
  }) {
    const holder =
      input.conflictingRecordId != null ? `record #${input.conflictingRecordId}` : "another record"
    super(`Source ${input.sourceType}:${input.sourceId} is already linked to ${holder}.`, {
      cause: input.cause,
    })
    this.name = "SourceConflictError"
    this.sourceType = input.sourceType
    this.sourceId = input.sourceId
    this.recordId = input.recordId
    this.conflictingRecordId = input.conflictingRecordId
  }
}

/** The uniqueness trigger's RAISE text: "Source <type>:<id> is already linked to
 *  record <id> (type <type>); cannot also link it to record <id>". Hasura prefixes
 *  "Uniqueness violation." and passes it through as the GraphQL error's message
 *  whatever its dev mode, so the message is what's matched. */
const CONFLICT = /is already linked to record(?: (\d+))?/

/** The conflicting record id when `err` is the trigger's violation, else undefined.
 *  Reads the GraphQL errors from either transport — Apollo's `graphQLErrors` or a
 *  plain `errors` array — and the error's own message. */
function sourceConflict(err: unknown): { conflictingRecordId: number | null } | undefined {
  const e = err as {
    graphQLErrors?: Array<{ message?: string }>
    errors?: Array<{ message?: string }>
    message?: string
  } | null
  const messages = [...(e?.graphQLErrors ?? e?.errors ?? []).map((x) => x?.message), e?.message]
  for (const message of messages) {
    const match = typeof message === "string" ? CONFLICT.exec(message) : null
    if (match) return { conflictingRecordId: match[1] ? Number(match[1]) : null }
  }
  return undefined
}

/** The record that holds this exact source, when `err` is the trigger's
 *  violation for it; otherwise undefined. A create that carries several sources
 *  can fail on any of them, so the caller names the one it means. */
export function sourceHolder(err: unknown, sourceType: string, sourceId: string): number | undefined {
  const e = err as {
    graphQLErrors?: Array<{ message?: string }>
    errors?: Array<{ message?: string }>
    message?: string
  } | null
  const messages = [...(e?.graphQLErrors ?? e?.errors ?? []).map((x) => x?.message), e?.message]
  const prefix = `Source ${sourceType}:${sourceId} is already linked to record `
  for (const message of messages) {
    if (typeof message !== "string") continue
    const at = message.indexOf(prefix)
    if (at === -1) continue
    const id = Number.parseInt(message.slice(at + prefix.length), 10)
    if (Number.isInteger(id)) return id
  }
  return undefined
}

export function createSourcesService({ query, mutate }: { query: Query; mutate: Mutate }) {
  return {
    list: async (recordId: number): Promise<ListedSource[]> =>
      (await query(fetchRecordSources, { recordId })).sources,
    /** The id of the record of `recordType` that holds this source, or null
     *  when none does (or none is visible to you). */
    holder: async (input: {
      sourceType: string
      sourceId: string
      recordType: string
    }): Promise<number | null> =>
      (await query(fetchSourceHolder, input)).sources[0]?.record_id ?? null,
    remove: async (
      recordId: number,
      sourceType: string,
      sourceId: string
    ): Promise<{ removedCount: number }> => {
      const res = await mutate(removeRecordSource, { recordId, sourceType, sourceId })
      // Sources are addressed by string pair, not row id — a typo'd pair (or a
      // row a permission filter hides) deletes nothing, and Hasura reports that
      // as a zero count, not an error. Every other delete in this package
      // count-checks; success for a delete that didn't happen is not success.
      const n = res.delete_record_sources?.affected_rows
      if (n === 0) {
        throw new Error(
          `sources.remove: removed nothing — no source (${sourceType}, ${sourceId}) on ` +
            `record ${recordId} is visible to you. Sources are addressed by string ` +
            `pair; check both values against sources.list(${recordId}).`
        )
      }
      return { removedCount: n ?? 0 }
    },
    /**
     * Undo a source's import: drop the record's claim on the source and roll
     * back what it infilled (data keys, description), keeping any edits made
     * after. Throws when the record doesn't claim the source. Returns the
     * record as it stands after.
     */
    detachAndRevert: async (
      recordId: number,
      sourceType: string,
      sourceId: string
    ): Promise<RecordRow> => {
      const res = await mutate(detachAndRevertSource, { recordId, sourceType, sourceId })
      const record = res.detach_and_revert_source?.[0]
      if (!record) {
        throw new Error(
          `sources.detachAndRevert: detaching (${sourceType}, ${sourceId}) from record ` +
            `${recordId} returned no record`
        )
      }
      return record
    },
    /**
     * Add one external-system source to a record. The ad-hoc door, beside
     * list/remove — bulk source FIELD writes go through the planner
     * (insertManyRecordSources in relations.ts:HANDLERS) and stamp
     * schema_key/schema_id; this one is an unqualified aggregate row, which is
     * what a source-attribution caller wants.
     */
    add: async (input: {
      recordId: number
      sourceType: string
      sourceId: string
      context?: Record<string, unknown> | null
      isPrimary?: boolean
      position?: number
      label?: string
    }): Promise<SourceRow> => {
      // One `object` variable rather than a variable per column, and that is
      // load-bearing rather than tidiness. A field-level variable is written
      // into the mutation text, so the field is ALWAYS present in the object;
      // an unprovided variable coerces to null and the column default never
      // applies. Verified against Hasura: `is_primary: $isPrimary` with no
      // value stores NULL, while omitting the column stores FALSE. Passing the
      // whole row as the variable is the only form where "omitted" survives —
      // JSON.stringify drops an undefined property, so the column is genuinely
      // absent from the insert. Same form the bulk documents already use.
      //
      // So the three optional columns pass through as-is, undefined included.
      // Do not coalesce them to null.
      const object = {
        record_id: input.recordId,
        source_type: input.sourceType,
        source_id: input.sourceId,
        // undefined, not {} — same reason as the three below, plus one of its
        // own. `{}` is not NULL, and the harmonic snapshot backfill selects
        // rows on `context IS NULL` (workers/harmonic/core/queue.js), so
        // defaulting to an empty object would permanently exclude every
        // source added here. `record_sources.context` is nullable with no
        // default, so omitting it stores NULL, which is what that job wants.
        context: input.context ?? undefined,
        is_primary: input.isPrimary,
        position: input.position,
        label: input.label,
      }
      let res: ResultOf<typeof addRecordSource>
      try {
        res = await mutate(addRecordSource, { object })
      } catch (err) {
        const conflict = sourceConflict(err)
        if (!conflict) throw err
        throw new SourceConflictError({
          sourceType: input.sourceType,
          sourceId: input.sourceId,
          recordId: input.recordId,
          conflictingRecordId: conflict.conflictingRecordId,
          cause: err,
        })
      }
      const created = res.insert_record_sources_one
      // UNIQUE (record_id, source_type, source_id) with no ON CONFLICT here:
      // a duplicate on this record raises in Hasura as-is; the same source on a
      // different record is a SourceConflictError, above. A null row instead means the insert fell
      // outside the caller's select filter — name that rather than return
      // undefined and let it surface three frames later.
      if (!created) {
        throw new Error(
          `sources.add: inserted no visible row for (${input.sourceType}, ` +
            `${input.sourceId}) on record ${input.recordId} — the row may have ` +
            `been created but is not visible to you.`
        )
      }
      return created
    },
  }
}
